import styled from 'styled-components';
import { lightenHex } from '../utils/annotationPresentation';

// Utility functions for tooltip positioning
function getTooltipTransform(placement, align) {
  const yTransform = placement === 'top' ? 'translateY(-100%)' : 'translateY(8px)';
  if (align === 'left') return `translateX(0) ${yTransform}`;
  if (align === 'right') return `translateX(-100%) ${yTransform}`;
  return `translateX(-50%) ${yTransform}`;
}

function getArrowLeft(align) {
  if (align === 'left') return '12px';
  if (align === 'right') return 'calc(100% - 12px)';
  return '50%';
}

export const TooltipContainer = styled.div`
  position: fixed;
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: ${props => props.theme.utils.borderRadius};
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  padding: 16px;
  width: 380px;
  max-width: 380px;
  z-index: 2147483647;
  pointer-events: auto;

  transform: ${props => getTooltipTransform(props.placement, props.align)};
  margin-top: ${props => (props.placement === 'top' ? '-8px' : '0')};

  &::after {
    content: '';
    position: absolute;
    ${props => (props.placement === 'top' ? 'top: 100%;' : 'bottom: 100%;')}
    left: ${props => getArrowLeft(props.align)};
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    ${props => (props.placement === 'top' ? `border-color: ${props.theme.colors.white} transparent transparent transparent;` : `border-color: transparent transparent ${props.theme.colors.white} transparent;`)}
  }

  &::before {
    content: '';
    position: absolute;
    ${props => (props.placement === 'top' ? 'top: 100%;' : 'bottom: 100%;')}
    left: ${props => getArrowLeft(props.align)};
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    ${props => (props.placement === 'top' ? `border-color: ${props.theme.colors.greyL} transparent transparent transparent;` : `border-color: transparent transparent ${props.theme.colors.greyL} transparent;`)}
  }
`;

export const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.theme.colors.greyL};
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const IconCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.theme.colors.greyXXXL};
  border: 2px solid ${props => props.bg || props.theme.colors.grey};
  color: ${props => props.bg || props.theme.colors.greyXXD};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
`;

export const HeaderTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 13px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const IconButton = styled.button`
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.grey};
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${props => props.theme.colors.greyXD};
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus {
    outline: none;
    background: ${props => props.theme.colors.greyXXXL};
    border-color: ${props => props.theme.colors.borderGrayHover};
    color: ${props => props.theme.colors.black};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.borderActive};
  }
`;

export const TooltipDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.greyXD};
  line-height: 1.5;
  margin-bottom: 8px;
  max-height: 60px;
  overflow-y: auto;
`;

export const TitleText = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.textPrimary};
  font-weight: 600;
  margin-bottom: 6px;
`;

export const TooltipMeta = styled.div`
  font-size: 10px;
  color: ${props => props.theme.colors.greyXD};

  .meta-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;

    .label {
      font-weight: 500;
    }
  }
`;

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const MetaIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.greyXXXL};
  color: ${props => props.theme.colors.greyXXD};
`;

export const TooltipActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${props => props.theme.colors.greyL};
`;

export const ActionButton = styled.button`
  background: ${props => props.primary ? props.theme.primaryColor : props.theme.colors.white};
  color: ${props => props.primary ? props.theme.colors.white : props.theme.colors.greyXXD};
  border: 1px solid ${props => props.primary ? props.theme.primaryColor : props.theme.colors.grey};
  border-radius: ${props => props.theme.utils.borderRadius};
  padding: 6px 10px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${props => props.primary ? props.theme.secondaryColor : props.theme.colors.greyXXXL};
  }

  &:active {
    transform: translateY(1px);
  }
`;

export const SeverityPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  margin-left: 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${(props) => {
    const s = ((props && props.severity) || '').toLowerCase();
    if (s === 'critical') return lightenHex(props.theme.colors.critical, 120);
    if (s === 'warning' || s === 'high' || s === 'poor') return lightenHex(props.theme.colors.warning, 120);
    if (s === 'medium' || s === 'fair') return lightenHex(props.theme.colors.moderate, 120);
    if (s === 'low' || s === 'good') return props.theme.colors.greenXXL;
    return props.theme.colors.greyXXXL;
  }};
  color: ${(props) => {
    const s = ((props && props.severity) || '').toLowerCase();
    if (s === 'critical') return props.theme.colors.critical;
    if (s === 'warning' || s === 'high' || s === 'poor') return props.theme.colors.warning;
    if (s === 'medium' || s === 'fair') return props.theme.colors.moderate;
    if (s === 'low' || s === 'good') return props.theme.colors.normal;
    return props.theme.colors.greyXXD;
  }};
`;
